import {
    BLACK_LIST_HANDLED_ERROR_NAMES,
    IGNORE_ERRORS_LIST,
    IGNORE_URLS_LIST,
    checkFbBots,
    checkIsOldDevices,
    getCustomTags,
    ErrorType,
} from '@user-portal/sentry-utils';
import * as Sentry from '@sentry/react';
import { ErrorEvent, EventHint, SeverityLevel } from '@sentry/react';
import { ENV } from '@user-portal/helpers';

import { getAmazonDefaultData } from './helpers';

import { errorType, Extras, IError, Tags, UserInfo } from './interfaces';
import { logger } from '@user-portal/logger';

declare interface iSentry {
    sentryDSN: string;
    env: ENV;
    release?: string;
    tracesSampleRate?: number;
    autoSessionTracking?: boolean;
}

export default class SentryClient {
    sentryDSN: string;
    env: ENV;
    release?: string;
    tracesSampleRate?: number;
    private isInnited: boolean;
    private sentry: any;
    private skipEnvironments: Set<ENV>;

    private static readonly SKIP_INITIALIZATION_ENVIRONMENTS = new Set([ENV.develop]);

    constructor({ sentryDSN, env, release, tracesSampleRate }: iSentry) {
        this.sentryDSN = sentryDSN;
        this.env = env || ENV.stage;
        this.release = release || '1.0.0';
        this.tracesSampleRate = tracesSampleRate || 1.0;
        this.isInnited = false;
        this.sentry = Sentry;
        this.skipEnvironments = SentryClient.SKIP_INITIALIZATION_ENVIRONMENTS;
    }

    private shouldSkipInitialization(envMode?: string): boolean {
        return this.skipEnvironments.has(envMode as ENV);
    }

    private logSystemSkipped(_systemName: string, reason: string): void {
        logger.info(`Skipping Sentry init (${reason})`);
    }

    init() {
        if (this.isInnited) return;

        if (this.shouldSkipInitialization(this.env)) {
            this.logSystemSkipped(this.env, 'nonprod environment');
            return;
        }

        Sentry.init({
            dsn: this.sentryDSN,
            integrations: [Sentry.browserTracingIntegration()],
            environment: this.env,
            debug: import.meta.env.ENV === ENV.stage,
            release: this.release,
            tracesSampleRate: this.tracesSampleRate,
            maxBreadcrumbs: 10,
            beforeSend: (event: ErrorEvent, hint: EventHint) => SentryClient.filterEventsBeforeSend(event, hint),
            ignoreErrors: IGNORE_ERRORS_LIST || [],
            denyUrls: IGNORE_URLS_LIST || [],
        });

        this.isInnited = true;
    }

    public logError(error: IError, type: ErrorType, errorLevel: SeverityLevel, extras?: Extras, customTags?: Tags) {
        const customTagsArr = customTags || getCustomTags(error, type, extras);

        SentryClient.getAnalyticData().then((analyticData) =>
            this.sentry.withScope((scope: Sentry.Scope) => {
                extras && scope.setExtras({ ...extras, ...analyticData });

                scope.setTag('ERROR_TYPE', type);
                scope.setTag('aws_id', analyticData?.aws_id || null);
                scope.setLevel(errorLevel);

                customTagsArr.length && customTagsArr.forEach(([tag, value]) => scope.setTag(tag, value));

                this.sentry.captureMessage(SentryClient.prepareErrorByCode(error, type));
            })
        );
    }

    public setUser(userData: UserInfo) {
        this.sentry.setUser({ ...userData });
    }

    public clearUser() {
        this.sentry.setUser(null);
    }

    private static prepareErrorByCode(error: IError, type: errorType) {
        return `[${type}] ${error}`;
    }

    private static async getAnalyticData() {
        return await getAmazonDefaultData();
    }

    private static filterEventsBeforeSend(event: ErrorEvent, hint: EventHint) {
        const error = hint.originalException;

        if (
            (typeof error === 'string' && SentryClient.matchSubstring(error)) ||
            checkIsOldDevices(event) ||
            checkFbBots(window.screen.width, window.screen.height)
        ) {
            return null;
        }

        return event;
    }

    private static matchSubstring(error: string) {
        return BLACK_LIST_HANDLED_ERROR_NAMES.some((item) => error.includes(item));
    }
}
